import InputField from 'components/fields/InputField';
import React, { useEffect, useState } from 'react';

const AddQA = ({ setTestData, testData, setDisable }) => {
  const [errors, setErrors] = useState([]);

  // Function to handle question input changes
  const handleInputChange = (event, index, field, optionIndex) => {
    const updatedQuestions = [...testData.questions];

    if (field === 'options') {
      updatedQuestions[index].options[optionIndex] = event.target.value;
    } else {
      updatedQuestions[index][field] = event.target.value;
    }

    setTestData({ ...testData, questions: updatedQuestions });
  };

  const handleCompletionTimeChange = (event) => {
    const { name, value } = event.target;
    setTestData((prevData) => {
      const [prevValue, prevUnit] = prevData.completionTime.split(' ');
      const completionTimeValue = name === 'value' ? value : prevValue;
      const completionTimeUnit = name === 'unit' ? value : prevUnit || 'hour'; // Default to 'hour' if undefined

      return {
        ...prevData,
        completionTime: `${completionTimeValue} ${completionTimeUnit}`,
      };
    });
  };

  // Function to handle title input change
  const handleTitleChange = (event) => {
    setTestData((prevData) => ({
      ...prevData,
      title: event.target.value,
    }));
  };

  // Function to add a new question
  const addQuestion = () => {
    const newQuestion = {
      questionText: '',
      options: ['', '', '', ''], // 4 options for MCQ
      correctAnswer: '',
    };

    setTestData((prevData) => ({
      ...prevData,
      questions: [...prevData.questions, newQuestion],
    }));
  };

  // Function to remove a question
  const removeQuestion = (index) => {
    const updatedQuestions = testData.questions.filter((_, i) => i !== index);
    setTestData((prevData) => ({
      ...prevData,
      questions: updatedQuestions,
    }));
  };

  useEffect(() => {
    const newErrors = [];

    // Validate test title
    if (!testData.title.trim()) {
      newErrors.push('Test title is required.');
    }

    // Validate each question
    testData.questions.forEach((question, index) => {
      if (!question.questionText.trim()) {
        newErrors.push(`Question ${index + 1} is incomplete.`);
      }
      if (
        question.type === 'mcq' &&
        question.options.some((option) => !option.trim())
      ) {
        newErrors.push(`Question ${index + 1} has empty MCQ options.`);
      }
      if (!question.correctAnswer.trim()) {
        newErrors.push(`Question ${index + 1} has no correct answer.`);
      }
      if (!question.marks) {
        newErrors.push(`Question ${index + 1} has no marks added`);
      }
    });

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [testData, setDisable]);

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pb-[5px] pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Add Assignment Details
      </h4>

      {/* Test Title Input */}
      <InputField
        extra="mb-3"
        label="Assignment Title"
        placeholder="Enter Assignment Title"
        id="testTitle"
        type="text"
        onChange={handleTitleChange}
        value={testData?.title}
      />

      {/* Completion Time Selector */}
      <div className="mb-3">
        <label className="text-sm font-medium text-gray-700">
          Completion Time
        </label>
        <div className="flex items-center gap-3">
          {/* Time Input */}
          <input
            name="value"
            type="number"
            min="0.5"
            step="0.5"
            placeholder="Enter Time"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            onChange={handleCompletionTimeChange}
            value={testData.completionTime.split(' ')[0] || ''}
          />

          {/* Unit Selection */}
          <select
            name="unit"
            className="rounded border border-gray-300 p-2 dark:!bg-navy-700"
            onChange={handleCompletionTimeChange}
            value={testData.completionTime.split(' ')[1] || 'hour'}
          >
            <option value="minute">Minutes</option>
            <option value="hour">Hours</option>
            <option value="day">Days</option>
          </select>
        </div>
      </div>

      {/* Questions List */}
      {testData.questions.map((question, index) => (
        <div key={index} className="mt-5">
          <h5 className="text-md font-semibold">Question {index + 1}</h5>

          {/* Question Text Input */}
          <InputField
            extra="mb-3"
            label="Question Text"
            placeholder="Enter Question Text"
            type="text"
            onChange={(e) => handleInputChange(e, index, 'questionText')}
            value={question?.questionText}
          />

          {/* MCQ Options Input (only for MCQ type) */}
          {testData.type === 'mcq' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {question.options.map((option, optionIndex) => (
                <InputField
                  key={optionIndex}
                  extra="mb-3"
                  label={`Option ${optionIndex + 1}`}
                  placeholder={`Option ${optionIndex + 1}`}
                  type="text"
                  onChange={(e) =>
                    handleInputChange(e, index, 'options', optionIndex)
                  }
                  value={option}
                />
              ))}
            </div>
          )}

          {/* Correct Answer Input */}
          <InputField
            extra="mb-3"
            label="Correct Answer"
            placeholder="Enter Correct Answer"
            type="text"
            onChange={(e) => handleInputChange(e, index, 'correctAnswer')}
            value={question?.correctAnswer}
          />

          <InputField
            extra="mb-3"
            label="Marks"
            placeholder="Enter Marks"
            type="text"
            value={question?.marks}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) {
                handleInputChange(e, index, 'marks');
              }
            }}
            className="w-full rounded border p-2"
          />

          {/* Remove Question Button */}
          <button
            className="mt-2 text-red-500"
            onClick={() => removeQuestion(index)}
          >
            Remove Question
          </button>
        </div>
      ))}

      {/* Add Question Button */}
      <button
        className="mt-5 rounded bg-blue-500 px-4 py-2 text-white"
        onClick={addQuestion}
      >
        + Add Question
      </button>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddQA;
